import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { staffApi, errMsg } from "@/lib/api";
import { ClassEditor } from "@/components/staff/JourneyClassEditor";
import { JourneyRegistrations } from "@/components/staff/JourneyRegistrations";
import { JourneyMembers } from "@/components/staff/JourneyMembers";

const EMPTY = { title: "", description: "", attendees: "", fee: 0, order: 99, active: true, schedules: [] };

export const AdminJourney = ({ role }) => {
  const isAdmin = role === "admin";
  const [classes, setClasses] = useState([]);
  const [regs, setRegs] = useState([]);
  const [members, setMembers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [v, setV] = useState(0);
  const load = useCallback(() => {
    const api = staffApi();
    Promise.all([api.get("/journey/classes"), api.get("/journey/registrations"), api.get("/journey/progress")])
      .then(([c, r, p]) => { setClasses(c.data); setRegs(r.data); setMembers(p.data); setV((x) => x + 1); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const saved = () => { setAdding(false); load(); };

  return (
    <div className="space-y-5" data-testid="admin-journey">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Discipleship Journey</h2>
        <p className="text-sm text-slate-500">Classes and seminars shown on the Journey page, in step order. Members tick what they've finished, and you can see their progress here.</p>
      </div>
      <Tabs defaultValue="classes">
        <TabsList className="h-auto flex-wrap justify-start rounded-2xl bg-white p-1 shadow-sm">
          <TabsTrigger value="classes" data-testid="journey-subtab-classes" className="rounded-xl">Classes ({classes.length})</TabsTrigger>
          <TabsTrigger value="registrations" data-testid="journey-subtab-registrations" className="rounded-xl">Registrations ({regs.length})</TabsTrigger>
          <TabsTrigger value="members" data-testid="journey-subtab-members" className="rounded-xl">Progress ({members.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="classes" className="mt-4 space-y-4">
          {isAdmin && !adding && <Button onClick={() => setAdding(true)} className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="journey-add-class"><Plus className="mr-1 h-4 w-4" />Add class / seminar</Button>}
          {adding && <ClassEditor c={{ ...EMPTY, order: classes.length + 1 }} onSaved={saved} isAdmin />}
          {classes.map((c) => <ClassEditor key={`${c.id}-${v}`} c={c} onSaved={load} isAdmin={isAdmin} />)}
        </TabsContent>
        <TabsContent value="registrations" className="mt-4"><JourneyRegistrations items={regs} isAdmin={isAdmin} onChanged={load} /></TabsContent>
        <TabsContent value="members" className="mt-4"><JourneyMembers items={members} classes={classes.filter((c) => c.active)} /></TabsContent>
      </Tabs>
    </div>
  );
};
